import { useCurrentUserActingPersonParty } from "Hooks/useCurrentUserActingPersonParty";
import { useCurrentUserParty } from "Hooks/useCurrentUserParty";
import { RelationType } from "Infrastructure/Api/Api";

type BeneficialOwnersResult = {
  isActingPartyOwner: boolean | null;
  ownerPartiesPublicIDs: Array<string>;
  isLoading: boolean;
  isError: boolean;
};

type Props = {
  excludeActingParty?: boolean;
};

export const useCurrentUsersPartyBeneficialOwners = ({
  excludeActingParty = false,
}: Props): BeneficialOwnersResult => {
  const {
    data: partyData,
    isLoading: isPartyLoading,
    isError,
  } = useCurrentUserParty();
  const { actingPartyData, isLoading: isActingPersonPartyLoading } =
    useCurrentUserActingPersonParty();

  const isLoading = isPartyLoading || isActingPersonPartyLoading;

  if (isLoading) {
    return {
      isActingPartyOwner: null,
      ownerPartiesPublicIDs: [],
      isLoading,
      isError,
    };
  }

  if (isError || !partyData) {
    return {
      isActingPartyOwner: null,
      ownerPartiesPublicIDs: [],
      isLoading,
      isError: true,
    };
  }

  const allOwnerPartiesPublicIDs =
    partyData.relatedParties
      .filter(
        ({ relationType }) => relationType === RelationType.BeneficialOwner,
      )
      .map(({ relatedPartyPublicID }) => relatedPartyPublicID) || [];

  const isActingPartyOwner = allOwnerPartiesPublicIDs.some(
    (publicID) => publicID === actingPartyData?.publicID,
  );

  const ownerPartiesPublicIDs = excludeActingParty
    ? allOwnerPartiesPublicIDs.filter(
        (publicID) => publicID !== actingPartyData?.publicID,
      )
    : allOwnerPartiesPublicIDs;

  return { isActingPartyOwner, ownerPartiesPublicIDs, isLoading, isError };
};
